import Call from "@/components/common/Call";
import ConsentCall from "@/features/consent/ConsentCall";
import { useDiaryByIdQuery } from "@/features/diaries/api/diaryQueries";
import MissingDiaryDetailsCall from "@/features/diaries/components/MissingDiaryDetailsCall";
import { useDiaryEditDisabled } from "@/features/diaries/context/DiaryEditDisabledContext";
import HistoryByTracker from "@/features/diaries/features/history/components/HistoryByTracker";
import { getBlockedFormExplanation, partitionFormsToDo } from "@/features/diaries/features/new/hooks/useFormsToDo";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import {
    buildPatientDisplayNameInput,
    getLocalizedPatientDisplayName,
} from "@/features/patients/utils/localizedPatientName";
import { ui } from "@patient-watch/ui";
import DiarySkeleton from "@/features/patients/components/DiarySkeleton";
import InterfaceButtons from "@/features/patients/features/interface/components/InterfaceButtons";
import { hooks as h } from "@/hooks";
import { Link, createFileRoute } from "@tanstack/react-router";
import DiaryOverviewDetailsCard from "@/features/patients/features/interface/components/DiaryOverviewDetailsCard";
import StateCard from "@/features/patients/features/interface/components/StateCard";
import { Route as TrackerPageRoute } from "@/routes/_app/patients/$diaryId/tracker/$trackerId/entry/$entryId/page/$pageIndex";
import { useAuth } from "@/utils/auth";
import { ClipboardEdit } from "lucide-react";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import { v4 as uuidv4 } from "uuid";

const PatientsDiaryErrorPage = () => {
    const copy = usePatientsCopy();
    return <div>{copy.error}</div>;
};

const PatientsDiaryIdPage = () => {
    const copy = usePatientsCopy();
    const { locale } = useAppLocale();
    const { diaryId } = Route.useParams();
    const { data: diary, isPending } = useDiaryByIdQuery(diaryId);
    const { data: profile } = useProfileByIdQuerySafe(diary?.patient_id);
    const auth = useAuth();
    const editDisabled = useDiaryEditDisabled();
    const { resetEntryValues } = useEntryValuesStore();
    const patientDisplayNameInput = buildPatientDisplayNameInput(profile, diary?.patient_info);

    h.useNav(
        diaryId
            ? [
                  { label: copy.patients, route: { to: "/patients" } },
                  {
                      label: getLocalizedPatientDisplayName(patientDisplayNameInput, locale) || copy.patientFallback,
                      route: {
                          to: "/patients/$diaryId",
                          params: { diaryId: diaryId },
                      },
                  },
              ]
            : [{ label: copy.patients, route: { to: "/patients" } }]
    );

    if (isPending) return <DiarySkeleton />;
    if (!diary) {
        return <div className="p-8 text-muted-foreground text-center">{copy.noDiaryFound}</div>;
    }

    const clinicianForms = partitionFormsToDo(diary.trackers_view, { roles: { isClinicianOnly: true } });
    const clinicianAttention = [...clinicianForms.toDo, ...clinicianForms.blocked];
    const hasClinicianTasks = !auth?.roles?.isPatientOnly && clinicianAttention.length > 0;
    const clinicianTaskCount = clinicianForms.taskCount;
    const dueFormsCount = clinicianForms.itemCount;
    const nextDueItem = clinicianForms.toDo[0] ?? clinicianForms.blocked[0];
    const nextDueForm = nextDueItem
        ? {
              title: nextDueItem.name,
              isLocked: clinicianForms.toDo.length === 0,
              blockedBy: getBlockedFormExplanation(nextDueItem),
              link: {
                  to: TrackerPageRoute.to,
                  params: {
                      diaryId,
                      trackerId: nextDueItem.data.tracker_id,
                      entryId: uuidv4(),
                      pageIndex: "1",
                  },
              },
          }
        : undefined;
    const nextBlockedBy = nextDueForm?.blockedBy;

    return (
        <section className="flex flex-col gap-y-2">
            <InterfaceButtons diaryId={diaryId} className="mt-0 pt-1 pb-0" />
            <div className="flex flex-col gap-y-3 pt-1">
                <DiaryOverviewDetailsCard diary={diary} profile={profile} showPatientIdentifiers />
                <StateCard diary={diary} />
                <ConsentCall diaryId={diaryId} routeContext="patients" />
                <MissingDiaryDetailsCall diary={diary} />

                {hasClinicianTasks && (
                    <Call
                        icon={<ClipboardEdit size={24} />}
                        words={copy.informationToComplete}
                        variant="blueSoft"
                        size="sm"
                    >
                        <p className="text-sm">{copy.clinicianTasks(clinicianTaskCount, dueFormsCount)}</p>
                        {nextBlockedBy ? (
                            <p className="text-muted-foreground text-sm">
                                Complete {nextBlockedBy} before starting the next clinician form.
                            </p>
                        ) : null}
                        {nextDueForm && (
                            <ui.Button
                                type="button"
                                size="sm"
                                variant="default"
                                disabled={editDisabled || nextDueForm.isLocked}
                                asChild={!editDisabled && !nextDueForm.isLocked}
                                data-testid="care-team-complete-button"
                            >
                                {editDisabled || nextDueForm.isLocked ? (
                                    <span>
                                        {dueFormsCount === 1
                                            ? copy.completeForm(nextDueForm.title)
                                            : copy.completeNextForm}
                                    </span>
                                ) : (
                                    <Link
                                        to={nextDueForm.link.to}
                                        params={nextDueForm.link.params}
                                        onClick={() => {
                                            resetEntryValues();
                                        }}
                                    >
                                        {dueFormsCount === 1
                                            ? copy.completeForm(nextDueForm.title)
                                            : copy.completeNextForm}
                                    </Link>
                                )}
                            </ui.Button>
                        )}
                    </Call>
                )}

                <HistoryByTracker diary={diary} hideMissingDetailsCall />
                <div className="h-16 shrink-0" aria-hidden />
            </div>
        </section>
    );
};

export const Route = createFileRoute("/_app/patients/$diaryId/")({
    component: PatientsDiaryIdPage,
    errorComponent: PatientsDiaryErrorPage,
});
